import { useEffect, useState } from "react";
import { IconFolderAdd, IconUserAdd } from "@humansignal/icons";
import { Button, Tooltip, themeAtom } from "@humansignal/ui";
import "./HomePage.prefix.css";
import { Link } from "react-router-dom";
import { useAPI, useUpdatePageTitle } from "@humansignal/core";
import { useAtom, useAtomValue } from "jotai";
import { useBranding } from "../../providers/BrandingProvider";
import { useTranslation } from "../../i18n/LocaleProvider";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { CreateProject } from "../CreateProject/CreateProject";
import { InviteModal } from "../Organization/MembersPage/InviteModal";
import { spaNavigate } from "../../utils/navigation";
import { isCapacitor } from "../../utils/host";
import { ExtensionBadgeIcons, type ExtensionBadge } from "../../components/ExtensionBadges/ExtensionBadges";
import type { Page } from "../types/Page";
import { JoinProjectModal } from "../Discover/JoinProjectModal";
import { RequestLanguageModal } from "./RequestLanguageModal";
import { LogoThumb } from "./OrglessHome";
import { OnboardingCard } from "./OnboardingCard";
import { creationDialogOpen, invitationOpen } from "./atoms";

// Default AfriAnnotate brand lockup (mark + wordmark), theme-aware. Used
// when the operator hasn't set a custom ``logo_url`` in Platform Settings.
// Mirrors the Menubar's asset choice so the footer matches the header.
const defaultLockup = (dark: boolean) =>
  dark ? "/static/brand/afriannotate/lockup-dark.png?v=6" : "/static/brand/afriannotate/lockup.png?v=6";

type PublicProject = {
  id: number;
  title: string | null;
  description: string | null;
  organization: { id: number; title: string } | null;
  task_count: number;
  contributor_count: number;
  contribution_mode: "members_only" | "open";
  already_joined: boolean;
  already_member: boolean;
  join_status?: "none" | "active" | "pending" | "member";
  logo?: string;
  consent_requirements?: Array<Record<string, unknown>>;
};

export const HomePage: Page = () => {
  const { t } = useTranslation();
  const branding = useBranding();
  const { user } = useAuth();
  // ABSOLUTE gate (owner rule): the signed-in home must NEVER render
  // without a session — not even one frame of "Welcome back" over
  // skeletons. Dead session on the JWT hosts → straight to login;
  // RouteGuard's server reconcile handles the tokens-look-live case.
  const sessionAlive = (() => {
    if (user) return true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (window as any).Capacitor === "undefined") return true; // web: cookie-authed
      const liveTok = (key: string) => {
        const tok = window.localStorage.getItem(key);
        if (!tok) return false;
        let b = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        while (b.length % 4) b += "=";
        const claims = JSON.parse(atob(b)) as { exp?: number };
        return typeof claims.exp === "number" && claims.exp > Date.now() / 1000;
      };
      return liveTok("afri:jwt:access") || liveTok("afri:jwt:refresh");
    } catch {
      return false;
    }
  })();
  useEffect(() => {
    if (!sessionAlive) {
      window.history.replaceState({}, "", "/auth/login");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [sessionAlive]);
  if (!sessionAlive) return null;
  const api = useAPI();
  const theme = useAtomValue(themeAtom);
  const isDark = String(theme).toLowerCase() === "dark";

  const canCreate = !!user && (user.is_staff || ["OW", "AD", "MA"].includes(user.active_organization_role));
  const canInvite = !!user && (user.is_staff || ["OW", "AD"].includes(user.active_organization_role));
  const canManageOrg = !!user && (user.is_staff || ["OW", "AD", "MA"].includes(user.active_organization_role));
  const isStaff = !!user && (user.is_staff || user.is_superuser);
  const onCapacitor = isCapacitor();

  const [modalIsOpen, setModalIsOpen] = useAtom(creationDialogOpen);
  const [invitationIsOpen, setInvitationIsOpen] = useAtom(invitationOpen);

  // Recent projects drive the "Continue working" section. ``null`` = still
  // loading (render neither the grid nor the empty state, to avoid a flash).
  // Gate on ``user?.id`` so the fetch waits for auth to hydrate (an unauth'd
  // call 401s and the global handler bounces the just-logged-in user).
  const [projects, setProjects] = useState<APIProject[] | null>(null);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api
      .callApi("projects", {
        // Ask for every field the card renders. Without an explicit
        // include the server picks a default projection; on mobile the
        // mirror stores whatever arrives, and a row missing
        // task_number/allow_on_mobile made cards show 0/0 or vanish.
        params: {
          page_size: 6,
          include:
            "id,title,color,logo,updated_at,created_at,task_number,finished_task_number,allow_on_mobile,organization,extensions",
        },
        suppressError: true,
      })
      .then((res: { results?: APIProject[] } | APIProject[] | undefined) => {
        if (cancelled) return;
        let list = (Array.isArray(res) ? res : (res?.results ?? [])) as APIProject[];
        // Capacitor: mirror the Projects page's mobile gate — only
        // projects the owner enabled for mobile. Without this, home
        // advertises projects the phone can't open (and My Projects
        // correctly hides), which reads as a data bug in testing.
        if (isCapacitor()) list = list.filter((p: any) => p.allow_on_mobile !== false);
        setProjects(list);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, user?.id]);

  // Open public projects this member could join as a contributor — the
  // same "advertise open projects" surface the orgless home shows.
  // Excludes projects they already joined or already belong to (own org).
  const [openProjects, setOpenProjects] = useState<PublicProject[] | null>(null);
  const [joinTarget, setJoinTarget] = useState<PublicProject | null>(null);
  const [requestLangOpen, setRequestLangOpen] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api
      .callApi("discover", { params: { page_size: 6, mode: "open" }, suppressError: true })
      .then((res: { results?: PublicProject[] } | PublicProject[] | undefined) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.results ?? []);
        setOpenProjects((list as PublicProject[]).filter((p) => !p.already_joined && !p.already_member));
      })
      .catch(() => {
        if (!cancelled) setOpenProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, user?.id]);

  useUpdatePageTitle("Home");

  const projectsKnown = projects !== null;
  const recentProjects = projects ?? [];
  const hasProjects = projectsKnown && recentProjects.length > 0;
  const firstName = user?.first_name || user?.email?.split("@")[0] || "there";

  const logoSrc = branding.logo_url || defaultLockup(isDark);
  const docsUrl = branding.docs_url;
  const discordUrl = branding.discord_url;

  // First-run signal: auth + projects have loaded and the user has none.
  // A brand-new user (nobody's added them to anything, they've made nothing)
  // gets the "what do you want to do?" nudge above the hero instead of a
  // blank dashboard. Existing users (any project) never see it. The card
  // self-suppresses once dismissed via its own localStorage flag.
  const isFirstRun = projectsKnown && recentProjects.length === 0;
  // Developer path target — fall back to the public docs site when the
  // operator hasn't set a custom docs_url in Platform Settings.
  const onboardingDocsUrl = docsUrl || "https://afriannotate.waraka.org/docs";

  return (
    <main className="lsf-home-page">
      {/* First-run onboarding: a dismissible, role-gated router shown only
          when the user has no projects yet. */}
      {isFirstRun && (
        <OnboardingCard canCreate={canCreate} docsUrl={onboardingDocsUrl} onCreate={() => setModalIsOpen(true)} />
      )}

      {/* Hero: greeting + one purpose line + the two primary actions. */}
      <section className="lsf-home-page__hero">
        <div className="lsf-home-page__hero-copy">
          <h1 className="lsf-home-page__hero-title">
            {t("pages.home.home_page.welcome_back", { name: firstName }, `Welcome back, ${firstName}`)}
          </h1>
          <p className="lsf-home-page__hero-sub">
            {canCreate
              ? t(
                  "pages.home.home_page.hero_sub_create",
                  undefined,
                  "Pick up where you left off, or start a new dataset.",
                )
              : t(
                  "pages.home.home_page.hero_sub_pickup",
                  undefined,
                  "Pick up where you left off. Your assigned projects are below.",
                )}
          </p>
        </div>
        <div className="lsf-home-page__hero-actions">
          {canCreate && (
            <Button size="medium" onClick={() => setModalIsOpen(true)} leading={<IconFolderAdd />}>
              {t("pages.home.home_page.create_project", undefined, "New project")}
            </Button>
          )}
          {canInvite && (
            <Button size="medium" look="outlined" onClick={() => setInvitationIsOpen(true)} leading={<IconUserAdd />}>
              {t("pages.home.home_page.invite_members", undefined, "Invite")}
            </Button>
          )}
        </div>
      </section>

      {/* Continue working */}
      <section className="lsf-home-page__sec">
        <div className="lsf-home-page__sec-head">
          <h2 className="lsf-home-page__sec-title">
            {t("pages.home.home_page.continue_working", undefined, "Continue working")}
          </h2>
          {recentProjects.length > 4 && (
            <Link className="lsf-home-page__link" to="/projects">
              {t("pages.home.home_page.all_projects", undefined, "See all →")}
            </Link>
          )}
        </div>

        {!projectsKnown ? (
          <div className="lsf-home-page__grid" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className="lsf-home-page__card" style={{ opacity: 0.5, minHeight: 118 }} />
            ))}
          </div>
        ) : hasProjects ? (
          <div className="lsf-home-page__grid">
            {recentProjects.slice(0, 4).map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        ) : canCreate ? (
          <div className="lsf-home-page__empty">
            <h3>{t("pages.home.home_page.create_first_project", undefined, "Create your first project")}</h3>
            {onCapacitor && (
              // Same hint as the Projects empty state — an owner whose
              // projects exist but aren't mobile-enabled must not read
              // this as "my projects are gone".
              <p>
                Existing projects appear on mobile once "Allow on mobile" is turned on in the project's General settings
                (web or desktop).
              </p>
            )}
            <p>
              {t(
                "pages.home.home_page.empty_copy_create",
                undefined,
                "Set up a labeling config, import your data, then invite your team or open it to the community.",
              )}
            </p>
            <div className="lsf-home-page__empty-actions">
              <Button onClick={() => setModalIsOpen(true)} leading={<IconFolderAdd />}>
                {t("pages.home.home_page.create_project", undefined, "New project")}
              </Button>
              {canInvite && (
                <Button look="outlined" onClick={() => setInvitationIsOpen(true)} leading={<IconUserAdd />}>
                  {t("pages.home.home_page.invite_members", undefined, "Invite")}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="lsf-home-page__empty">
            <h3>{t("pages.home.home_page.no_projects_yet", undefined, "No projects yet")}</h3>
            <p>
              {t(
                "pages.home.home_page.empty_copy_annotator",
                undefined,
                "Once you're added to a project it'll show up here. You can also browse open projects to contribute to.",
              )}
            </p>
            <div className="lsf-home-page__empty-actions">
              <Button onClick={() => spaNavigate("/discover")}>
                {t("pages.home.home_page.browse_discover", undefined, "Browse Discover")}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Open to join: public projects this member can contribute to,
          advertised the same way the orgless home does. */}
      {openProjects && openProjects.length > 0 && (
        <section className="lsf-home-page__sec">
          <div className="lsf-home-page__sec-head">
            <h2 className="lsf-home-page__sec-title">
              {t("pages.home.home_page.open_to_join", undefined, "Open to join")}
            </h2>
            {openProjects.length > 4 && (
              <button
                type="button"
                className="lsf-home-page__link"
                onClick={() => spaNavigate("/discover")}
                style={{ background: "none", border: 0, cursor: "pointer" }}
              >
                {t("pages.home.home_page.see_all", undefined, "See all →")}
              </button>
            )}
          </div>
          <div className="lsf-home-page__pubgrid">
            {openProjects.slice(0, 4).map((p) => (
              <div key={p.id} className="lsf-home-page__pubcard">
                <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                  <LogoThumb src={p.logo} title={p.title} />
                  <div style={{ minWidth: 0 }}>
                    <h4 className="lsf-home-page__pubcard-title">
                      {p.title ?? t("pages.home.home_page.untitled_project", undefined, "Untitled project")}
                    </h4>
                    {p.organization?.title ? (
                      <span className="lsf-home-page__pubcard-org">
                        {t("pages.home.home_page.by_org", { org: p.organization.title }, `by ${p.organization.title}`)}
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="lsf-home-page__pubcard-desc">
                  {p.description || t("pages.home.home_page.no_description", undefined, "No description provided.")}
                </p>
                <div className="lsf-home-page__pubcard-meta">
                  <span className="lsf-home-page__pubcard-stats">
                    {t(
                      "pages.home.home_page.stats",
                      { tasks: p.task_count.toLocaleString(), contributors: p.contributor_count.toLocaleString() },
                      `${p.task_count.toLocaleString()} tasks, ${p.contributor_count.toLocaleString()} contributors`,
                    )}
                  </span>
                  {p.join_status === "pending" ? (
                    <Button variant="neutral" look="outlined" size="small" disabled>
                      {t("pages.home.home_page.requested_label", undefined, "Requested")}
                    </Button>
                  ) : (
                    <Button variant="primary" size="small" onClick={() => setJoinTarget(p)}>
                      {t("pages.home.home_page.join_label", undefined, "Join")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Promo row: publish-to-Discover + contribute-a-language, side by
          side. Both render on Capacitor too — the mobile Discover feed is
          server-filtered to mobile-annotatable projects (allow_on_mobile),
          so the promo leads somewhere useful. */}
      <section className="lsf-home-page__sec">
        <div className="lsf-home-page__promos">
          <div className="lsf-home-page__discover">
            <div>
              <h3 className="lsf-home-page__discover-title">
                {t("pages.home.home_page.discover_title", undefined, "Grow your dataset with the community")}
              </h3>
              <p className="lsf-home-page__discover-text">
                {t("pages.home.home_page.discover_text_pre", undefined, "Publish a project to")} <b>Discover</b>{" "}
                {t(
                  "pages.home.home_page.discover_text_post",
                  undefined,
                  "and let contributors across the platform help annotate, or browse open projects you can join.",
                )}
              </p>
            </div>
            <Button look="outlined" size="small" onClick={() => spaNavigate("/discover")}>
              {t("pages.home.home_page.browse_discover_arrow", undefined, "Browse Discover →")}
            </Button>
          </div>

          <div className="lsf-home-page__discover">
            <div>
              <h3 className="lsf-home-page__discover-title">
                {t("pages.home.home_page.contribute_language_title", undefined, "Contribute a language")}
              </h3>
              <p className="lsf-home-page__discover-text">
                {t(
                  "pages.home.home_page.contribute_language_text",
                  undefined,
                  "Speak a language we don't support yet? Request it and help translate the interface with other volunteers.",
                )}
              </p>
            </div>
            <Button look="outlined" size="small" onClick={() => setRequestLangOpen(true)}>
              {t("pages.home.home_page.request_language_arrow", undefined, "Request a language →")}
            </Button>
          </div>
        </div>
      </section>

      {/* Utility footer: replaces the old quick-actions + resources cards. */}
      <footer className="lsf-home-page__util">
        <nav className="lsf-home-page__util-nav">
          {canManageOrg && !onCapacitor && (
            <Link to="/organization">{t("nav.organization", undefined, "Organization")}</Link>
          )}
          {isStaff && !onCapacitor && <Link to="/platform">{t("nav.platform", undefined, "Platform")}</Link>}
          {user?.id && <Link to={`/users/${user.id}`}>{t("nav.account", undefined, "Account")}</Link>}
          {docsUrl && (
            <a href={docsUrl} target="_blank" rel="noreferrer">
              {t("pages.home.home_page.documentation", undefined, "Documentation")}
            </a>
          )}
          {discordUrl && (
            <a href={discordUrl} target="_blank" rel="noreferrer">
              Discord
            </a>
          )}
        </nav>
        <span className="lsf-home-page__brand">
          <img className="lsf-home-page__brand-logo" src={logoSrc} alt={`${branding.name} logo`} />
        </span>
      </footer>

      {modalIsOpen && <CreateProject onClose={() => setModalIsOpen(false)} />}
      {requestLangOpen && <RequestLanguageModal onClose={() => setRequestLangOpen(false)} />}
      <InviteModal opened={invitationIsOpen} onClosed={() => setInvitationIsOpen(false)} />
      {joinTarget && (
        <JoinProjectModal
          project={joinTarget}
          onClose={() => setJoinTarget(null)}
          onJoined={(id, status) => {
            setJoinTarget(null);
            if (status === "pending") {
              setOpenProjects((prev) => prev?.map((p) => (p.id === id ? { ...p, join_status: "pending" } : p)) ?? prev);
              return;
            }
            spaNavigate(`/projects/${id}`);
          }}
        />
      )}
    </main>
  );
};

HomePage.title = "Home";
HomePage.path = "/";
HomePage.exact = true;

function initialsFor(title: string) {
  const clean = (title ?? "").trim();
  if (!clean) return "?";
  const parts = clean.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || clean[0].toUpperCase();
}

type TFn = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

function formatRelative(iso: string | null | undefined, t: TFn) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return t("pages.home.home_page.time_just_now", undefined, "just now");
  if (mins < 60) return t("pages.home.home_page.time_minutes", { n: mins }, `${mins}m ago`);
  const hours = Math.round(mins / 60);
  if (hours < 24) return t("pages.home.home_page.time_hours", { n: hours }, `${hours}h ago`);
  const days = Math.round(hours / 24);
  if (days < 7) return t("pages.home.home_page.time_days", { n: days }, `${days}d ago`);
  const weeks = Math.round(days / 7);
  if (weeks < 5) return t("pages.home.home_page.time_weeks", { n: weeks }, `${weeks}w ago`);
  const months = Math.round(days / 30);
  if (months < 12) return t("pages.home.home_page.time_months", { n: months }, `${months}mo ago`);
  return t("pages.home.home_page.time_years", { n: Math.round(days / 365) }, `${Math.round(days / 365)}y ago`);
}

function ProjectCard({ project }: { project: APIProject }) {
  const { t } = useTranslation();
  // A mirrored row can lack the counts entirely (written from a
  // projection that omitted them) — that's "unknown", not "0 / 0".
  // Render the bar without numbers until the revalidated row lands.
  const countsKnown = project.task_number != null;
  const finished = project.finished_task_number ?? 0;
  const total = project.task_number ?? 0;
  const pct = total > 0 ? Math.round((finished / total) * 100) : 0;
  const white = "#FFFFFF";
  const color = project.color && project.color !== white ? project.color : "var(--color-primary-surface)";
  const logo = (project as { logo?: string }).logo;
  // Offline (or an expired signed URL) breaks the <img>; the browser
  // paints a broken-image glyph inside the badge. Fall back to the
  // initials tile instead — same look as projects with no logo.
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = !!logo && !logoFailed;
  const updated = formatRelative(project.updated_at ?? project.created_at, t);

  return (
    <Link className="lsf-home-page__card" to={`/projects/${project.id}`} data-external aria-label={project.title}>
      <div className="lsf-home-page__card-top">
        <span
          className="lsf-home-page__badge"
          style={{ background: showLogo ? "var(--color-neutral-background)" : color, padding: 0, overflow: "hidden" }}
          aria-hidden
        >
          {showLogo ? (
            <img
              src={logo}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            initialsFor(project.title)
          )}
        </span>
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <Tooltip title={project.title}>
            <h4 className="lsf-home-page__card-title">{project.title}</h4>
          </Tooltip>
          {updated ? (
            <span className="lsf-home-page__card-lang">
              {t("pages.home.home_page.updated_time", { time: updated }, `Updated ${updated}`)}
            </span>
          ) : null}
        </div>
        {/* What an installed extension says about the project, as icons. */}
        <ExtensionBadgeIcons badges={(project as { extensions?: { badges?: ExtensionBadge[] } }).extensions?.badges} />
      </div>
      <div className="lsf-home-page__bar" aria-hidden>
        <span className="lsf-home-page__bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="lsf-home-page__card-foot">
        <span className="lsf-home-page__nums">
          {countsKnown
            ? t(
                "pages.home.home_page.tasks_progress",
                { done: finished.toLocaleString(), total: total.toLocaleString() },
                `${finished.toLocaleString()} / ${total.toLocaleString()} tasks`,
              )
            : "\u2026"}
        </span>
        <span className="lsf-home-page__pct">{countsKnown ? `${pct}%` : ""}</span>
      </div>
    </Link>
  );
}
